import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, Bot } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SalesChat() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: '¿Meta o Google Ads? Te ayudo en segundos a ver qué campañas te hacen perder dinero 🚀'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mostrar preview tras 2s — NO abre el chat completo, solo el bocadillo
  useEffect(() => {
    const dismissed = sessionStorage.getItem('sales_preview_dismissed');
    if (!dismissed) {
      const timer = setTimeout(() => setShowPreview(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismissPreview = (e) => {
    e?.stopPropagation();
    setShowPreview(false);
    sessionStorage.setItem('sales_preview_dismissed', '1');
  };

  const openChat = () => {
    setIsOpen(true);
    setShowPreview(false);
    sessionStorage.setItem('sales_preview_dismissed', '1');
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = { id: Date.now(), role: 'user', content: input.trim() };
    const context = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/support/sales-chat`, {
        message: input.trim(), context, user_stage: 'visitor'
      });
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: '¡Pruébalo gratis ahora! Regístrate en menos de 30 segundos 🚀' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* BURBUJA + PREVIEW (estado cerrado) */}
      <AnimatePresence>
        {!isOpen && (
          <div className="fixed bottom-6 right-6 z-50 flex items-end gap-2">
            {/* Preview bocadillo */}
            <AnimatePresence>
              {showPreview && (
                <motion.div
                  initial={{ opacity: 0, x: 10, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 10, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  onClick={openChat}
                  className="relative mb-1 cursor-pointer bg-white rounded-2xl rounded-br-sm shadow-lg border border-gray-200 px-3 py-2 max-w-[180px] hover:shadow-xl transition-shadow"
                >
                  <button
                    onClick={dismissPreview}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600"
                    aria-label="Cerrar"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <p className="text-xs text-gray-700 leading-snug">
                    ¿Necesitas ayuda? 👋
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Soporte AdPulse
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Avatar burbuja */}
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              onClick={openChat}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              className="relative w-14 h-14 rounded-full bg-gradient-to-br from-ai-glow to-brand-meta text-white shadow-lg shadow-ai-glow/30 flex items-center justify-center"
              aria-label="Abrir chat"
            >
              <Bot className="w-7 h-7" />
              {/* Punto online */}
              <span className="absolute bottom-0.5 right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
            </motion.button>
          </div>
        )}
      </AnimatePresence>

      {/* VENTANA DE CHAT (estado abierto) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[300px] sm:w-[340px] h-[440px] sm:h-[480px] rounded-2xl shadow-2xl flex flex-col overflow-hidden bg-white border border-gray-200"
          >
            {/* Header compacto */}
            <div className="bg-gradient-to-r from-ai-glow to-brand-meta px-3 py-2.5 flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="relative w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                  <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-400 border border-white rounded-full" />
                </div>
                <div>
                  <h3 className="text-white text-sm font-semibold leading-tight">Soporte AdPulse</h3>
                  <p className="text-white/70 text-[10px] leading-tight">En línea</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Minimizar"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-gray-50">
              {messages.map(message => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs whitespace-pre-wrap leading-relaxed ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-br-sm'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                  }`}>
                    {message.content}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-white shadow-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* CTA discreto */}
            <button
              onClick={() => navigate('/register')}
              className="mx-3 mb-2 py-2 rounded-xl text-white text-[11px] font-semibold bg-gradient-to-r from-ai-glow to-brand-meta hover:opacity-90 transition-opacity"
            >
              🚀 Empieza gratis — sin tarjeta
            </button>

            {/* Input */}
            <div className="px-3 pb-3 pt-1 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleSend()}
                  placeholder="Escribe tu pregunta…"
                  className="flex-1 px-3 py-2 rounded-full text-xs bg-gray-100 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ai-glow/50"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="w-8 h-8 rounded-full bg-gradient-to-r from-ai-glow to-brand-meta text-white flex items-center justify-center disabled:opacity-40"
                  aria-label="Enviar"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
